import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

interface PipelineSearchProps {
    onSearch: (searchTerm: string) => void;
    isLoading?: boolean;
    placeholder?: string;
    className?: string;
    currentSearchTerm?: string;
}

const PipelineSearch: React.FC<PipelineSearchProps> = ({
    onSearch,
    isLoading = false,
    placeholder = "Search leads by name, email, or phone...",
    className = "",
    currentSearchTerm = ""
}) => {
    const [searchTerm, setSearchTerm] = useState(currentSearchTerm);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync internal state with prop changes
    useEffect(() => {
        setSearchTerm(currentSearchTerm);
    }, [currentSearchTerm]);

    const handleSearch = () => {
        onSearch(searchTerm.trim());
    };

    const handleClear = () => {
        setSearchTerm('');
        onSearch('');
        inputRef.current?.focus();
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className={cn("relative flex items-center", className)}>
            <div className={cn(
                "relative flex items-center w-full max-w-md transition-all duration-200",
                "bg-white border rounded-lg shadow-sm",
                isFocused
                    ? "border-brandColor ring-2 ring-brandColor/20 shadow-md"
                    : "border-gray-200 hover:border-gray-300"
            )}>
                {/* Search Icon */}
                <div className="absolute left-3 text-gray-400">
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Search className="h-4 w-4" />
                    )}
                </div>

                {/* Input Field */}
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder={placeholder}
                    className="w-full pl-10 pr-20 py-2.5 text-sm border-0 rounded-lg focus:outline-none focus:ring-0 bg-transparent"
                    disabled={isLoading}
                />

                {/* Action Buttons */}
                <div className="absolute right-2 flex items-center gap-1">
                    {searchTerm && (
                        <button
                            onClick={handleClear}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                            title="Clear search"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}

                    <button
                        onClick={handleSearch}
                        disabled={isLoading || !searchTerm.trim()}
                        className={cn(
                            "px-3 py-1 text-xs font-medium rounded transition-all",
                            searchTerm.trim() && !isLoading
                                ? "bg-brandColor text-white hover:bg-brand-hover shadow-sm"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        )}
                    >
                        Search
                    </button>
                </div>
            </div>

            {/* Search Tips */}
            {isFocused && !searchTerm && (
                <div className="absolute top-full left-0 mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-80">
                    <div className="text-xs text-gray-600">
                        <p className="font-medium mb-1">Search Tips:</p>
                        <ul className="space-y-1 text-gray-500">
                            <li>• Search by name, email, or phone number</li>
                            <li>• Use partial matches (e.g., "john" finds "John Doe")</li>
                            <li>• Press Enter or click Search to apply</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PipelineSearch;